/**
 * A CUSTOMER'S MARK — and, exactly as with a provider, the honest statement
 * that Cluvik does not have one.
 *
 * ─── WHAT THIS REPLACED, AND WHY IT WAS WRONG ────────────────────────────
 *
 * The tenants list drew every row's avatar as:
 *
 *     bg-gradient-to-br from-indigo-500 to-purple-600   +   name[0]
 *
 * Three separate faults in one 40px square, and none of them visible to a
 * reviewer who is looking at whether the screen is pretty:
 *
 * 1. THE GRADIENT IS NOT ON THE CLUVIK RAMP. `indigo-500` (#6366F1) and
 *    `purple-600` (#9333EA) are Tailwind's own scale. The approved system's
 *    violet is #6030F0 and its indigo is #4F46E5, and the difference is not
 *    subtle when the two sit on one screen. This was a second visual language
 *    on the busiest page in the product — the exact defect already recorded
 *    against the Create Tenant form's six invented header tiles, surviving in
 *    the one place nobody re-looked at.
 *
 * 2. IT WAS A DIMENSIONAL TREATMENT ON EVERY ROW. In this system a gradient
 *    tile says IDENTITY — a place. Forty of them down a table say nothing at
 *    all, and the one mark on the page that is actually an area identity has
 *    to compete with forty imitations of itself.
 *
 * 3. THE COLOUR WAS THE SAME FOR EVERY TENANT, so the only thing separating
 *    one row's mark from another's was a SINGLE LETTER. Two customers whose
 *    names begin with the same letter got literally identical artwork.
 *
 * ─── WHAT THIS DRAWS INSTEAD, AND WHAT IT REFUSES TO ─────────────────────
 *
 * A neutral Cluvik tile from `neutralTile.jsx` — the same object the provider
 * mark draws, so the two cannot drift — carrying up to two initials, at a ramp
 * position hashed from the tenant's ID.
 *
 * THE ID, NOT THE NAME. A tenant renamed keeps its mark. An operator who has
 * learned to find one customer by the colour of its tile does not have to
 * relearn it because somebody corrected a spelling, and two tenants sharing an
 * initial no longer share a mark.
 *
 * AND IT IS NOT THE CUSTOMER'S LOGO. Cluvik holds no customer artwork, and a
 * generated tile must never be mistaken for one: the `<title>` says
 * "Cluvik placeholder mark" in the same words the provider tile uses, and
 * `data-artwork="neutral"` states it to a test. When real customer artwork is
 * ever uploaded, it belongs in a slot here on the model of `BRAND_ARTWORK` —
 * and, like that one, it should be a decision somebody makes on purpose.
 */
import React from 'react';
import { IDENTITY_SIZES, identityPx } from '../tokens/identityTokens';
import NeutralTileArt, { TILE_VIEWBOX, initialsFrom, rampFromKey } from './neutralTile';

/**
 * @param {string} tenantId  The tenant's stable id. Drives the ramp position;
 *                           an absent one falls back to the name, which is
 *                           worse (it moves on a rename) and is said so here
 *                           rather than being silently equivalent.
 * @param {string} name      Operator-facing tenant name — the initials, and the
 *                           accessible name when `labelled`.
 * @param {string|number} size Identity size token or pixels. Never below xs.
 * @param {boolean} labelled Give the mark an accessible name. Leave OFF when
 *                           the tenant's name is written beside it, which on a
 *                           table row it always is.
 */
export default function TenantMark({
  tenantId, name, size = 'lg', labelled = false, className,
}) {
  const px = Math.max(identityPx(size), IDENTITY_SIZES.xs);
  const rampKey = rampFromKey(tenantId || name);
  const initials = initialsFrom(name);

  return (
    <svg
      width={px}
      height={px}
      viewBox={`0 0 ${TILE_VIEWBOX} ${TILE_VIEWBOX}`}
      className={className}
      role={labelled ? 'img' : 'presentation'}
      aria-label={labelled ? `${name || 'Tenant'} — no customer artwork available` : undefined}
      aria-hidden={labelled ? undefined : true}
      focusable="false"
      data-tenant={String(tenantId || '')}
      /* The same machine-readable statement the provider mark makes. A
         screenshot cannot tell a generated tile from a customer's real logo;
         this attribute can. */
      data-artwork="neutral"
    >
      <title>{`${name || 'Tenant'} (Cluvik placeholder mark)`}</title>
      <NeutralTileArt rampKey={rampKey} initials={initials} />
    </svg>
  );
}
